import React, { useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';

import fontService, { FontWeight, TextType } from 'services/FontService';

import { Icon, IconNames, Button } from 'atoms';
import {
  getTableOpened,
  getWebSocketConnection,
  getSocketDisconnected,
  getSelectedTableData,
} from 'core/selectors';
import { useConnectionInfo } from 'hooks';
import { getSocketError, getSocketStatus } from 'core/selectors/webSocketSelector';

export const NotAvailablePage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { id: tableId } = useParams();

  const connectionInfo = useConnectionInfo();
  const socketStatus = useSelector(getSocketStatus);
  const socketError = useSelector(getSocketError);

  const tableOpened = useSelector(getTableOpened);
  const webSocketConnection = useSelector(getWebSocketConnection);
  const isSocketDisconnected = useSelector(getSocketDisconnected);
  const selectedTableData = useSelector(getSelectedTableData);

  const onBackToMainClick = useCallback(() => {
    navigate('/', { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (
      tableOpened &&
      webSocketConnection &&
      !isSocketDisconnected &&
      (selectedTableData?.game_id || tableId)
    ) {
      navigate(`/tables/${selectedTableData?.game_id || tableId}`, { replace: true });
    }
  }, [
    tableOpened,
    webSocketConnection,
    isSocketDisconnected,
    selectedTableData,
    navigate,
    dispatch,
    tableId,
  ]);

  return (
    <div>
      <Header>
        <Text>Dealer User Interface ({process.env.REACT_APP_PLATFORM_VERSION})</Text>
      </Header>

      <NoAvailableContainer>
        <Icon name={IconNames.NoAvailablePage} />
        <NoAvailableTitle>This page is not available</NoAvailableTitle>
        <CustomButton onClick={onBackToMainClick} type="button">
          Main Page
        </CustomButton>

        <ItemBox>
          <ItemInner>
            <ItemText>Connection type: {connectionInfo?.effectiveType}</ItemText>
            <ItemText>Ping (RTT): {connectionInfo?.rtt} ms</ItemText>
            {(socketStatus?.length > 0 || socketError?.length > 0) && (
              <div>
                <ItemText>Socket status + error history:</ItemText>
                <ListUl>
                  {Array.from({
                    length: Math.max(socketStatus?.length || 0, socketError?.length || 0),
                  }).map((_, i) => (
                    <li key={`entry-${i}`}>
                      <ItemText>
                        {i + 1}: Status: {socketStatus?.[i] ?? '—'} | Error:{' '}
                        {socketError?.[i] ?? '—'}
                      </ItemText>
                    </li>
                  ))}
                </ListUl>
              </div>
            )}
          </ItemInner>
        </ItemBox>
      </NoAvailableContainer>
    </div>
  );
};

const NoAvailableContainer = styled.div`
  width: 100%;
  max-width: 1152px;
  min-height: calc(100vh - 345px);
  margin-top: 16px;
  display: flex;
  margin: 16px auto 0;
  flex-direction: column;
  row-gap: 32px;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  z-index: 10;
`;

const Header = styled.div`
  width: 100%;
  height: 48px;
  background: #121011;
`;

const Text = styled.p`
  width: 100%;
  height: 100%;
  max-width: 1152px;
  ${fontService.text({ size: 10, weight: FontWeight.regular })};
  color: ${({ theme: { primaryTextColor } }) => primaryTextColor};
  margin: 0 auto;
  display: flex;
  align-items: center;
`;

const NoAvailableTitle = styled.p`
  ${fontService.text({ size: 24, weight: FontWeight.bold })};
  color: ${({ theme: { primaryTextColor } }) => primaryTextColor};
  text-transform: uppercase;
`;

const CustomButton = styled(Button)`
  width: 240px;
  text-transform: uppercase;
  background: #BB9370;
  border: 1px solid rgba(187, 147, 112, 0.24);
  &:hover {
    background: #D4BB93;
    border: 1px solid rgba(212, 187, 147, 0.24);
    cursor: pointer;
`;

const ItemBox = styled.div`
  display: flex;
  align-items: center;
`;

const ItemInner = styled.div``;

const ItemText = styled.p`
  ${fontService.text({ type: TextType.extraSmall })};
  ${({ theme: { text } }) => `
    color: ${text.secondaryDisabled};
    line-height: 16px;
  `}
`;

const ListUl = styled.ul`
  padding: 0;
  list-style: none;
`;
